/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import {Box} from "@mui/material";
import Menu from "@mui/material/Menu";
import {QMenu} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QMenu";
import {QTableMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QTableMetaData";
import {QRecord} from "@temple-of-syrinx/qqq-frontend-core/lib/model/QRecord";
import {QActionButton, QActionsMenuButton} from "qqq/components/buttons/DefaultButtons";
import {ActionMenuActions, ActionMenuItem, ItemsShownInMenu} from "qqq/components/menus/ActionMenuItems";
import React, {useState} from "react";

export type RecordViewMenuActions = ActionMenuActions;
export {ActionMenuItem as RecordViewMenuItem, ItemsShownInMenu} from "qqq/components/menus/ActionMenuItems";


/////////////////////////////////////////////////
// component for the multiple additional menus //
/////////////////////////////////////////////////
interface RecordViewAdditionalMenusProps
{
   tableMetaData: QTableMetaData,
   record?: QRecord,
   actions: ActionMenuActions,
}

export function RecordViewAdditionalMenus({tableMetaData, record, actions}: RecordViewAdditionalMenusProps): JSX.Element
{
   const menus = (tableMetaData.menus ?? []).filter(m => m.slot == "VIEW_SCREEN_ADDITIONAL");

   if (!menus)
   {
      return <></>;
   }

   return (<Box>
      {menus.map((menu, index) => <RecordViewMenu key={index} tableMetaData={tableMetaData} menu={menu} record={record} actions={actions} />)}
   </Box>);
}


/////////////////////////////////
// component for a single menu //
/////////////////////////////////
interface RecordViewMenuProps
{
   tableMetaData: QTableMetaData,
   menu: QMenu,
   record?: QRecord,
   actions: ActionMenuActions,
   accordionStorageScope?: "query" | "view",
}

export function RecordViewMenu({tableMetaData, menu, record, actions, accordionStorageScope = "view"}: RecordViewMenuProps): JSX.Element
{
   const [menuElement, setMenuElement] = useState(null);
   const openMenu = (event: any) => setMenuElement(event.currentTarget);
   const closeMenu = () => setMenuElement(null);

   const itemsShown = new ItemsShownInMenu();

   ///////////////////////////////////////////////////////////////////////////////////////////
   // a menu that holds exactly one run-process item is a one-click action: render it as a  //
   // plain button that runs the process, rather than a dropdown with a single entry.  Same //
   // suppression rule as the menu item: if the process isn't in this user's metaData       //
   // (permissions), render nothing.                                                        //
   ///////////////////////////////////////////////////////////////////////////////////////////
   if (menu.items?.length === 1 && menu.items[0].itemType === "RUN_PROCESS")
   {
      const processName = menu.items[0].values?.get("processName") as string;
      const process = actions.getMetaData?.()?.processes?.get(processName);
      if (actions.runProcess == null || !process)
      {
         return <></>;
      }
      const label = menu.label ?? menu.items[0].label ?? process.label ?? processName;
      return (
         <Box display="inline-block" data-qqq-id="record-view-direct-action-button">
            <QActionButton label={label} qIcon={menu.icon ?? menu.items[0].icon} qqqId={`run-process-${processName}`} onClickHandler={() => actions.runProcess(processName)} />
         </Box>
      );
   }

   return <>
      <Box display="inline-block" data-qqq-id="record-view-actions-menu-button">
         <QActionsMenuButton isOpen={menuElement} onClickHandler={openMenu} label={menu.label} qIcon={menu.icon} />
      </Box>
      <Menu
         anchorEl={menuElement}
         anchorOrigin={{vertical: "bottom", horizontal: "right",}}
         transformOrigin={{vertical: "top", horizontal: "right",}}
         open={Boolean(menuElement)}
         onClose={closeMenu}
         keepMounted
         data-qqq-id="record-view-actions-menu"
      >
         {menu.items.map((item, index) => (
            <ActionMenuItem key={index} tableMetaData={tableMetaData} menuItem={item} record={record} actions={actions} closeMenu={closeMenu} itemsShown={itemsShown} accordionStorageScope={accordionStorageScope} />
         ))}
      </Menu>
   </>;
}
